import { useState, useCallback } from 'react';
import {
  FirstAid,
  Warning,
  Eye,
  ShieldCheck,
  PencilSimple,
  FloppyDisk,
  Spinner,
  X,
  Plus,
  Trash,
  FileText,
  Stethoscope,
  Note,
  Paperclip,
  DownloadSimple,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';
import { API, formatDate } from './constants';

const TIPO_CONFIG = {
  avaliacao: { label: 'Avaliacao', color: '#7C3AED', bg: '#F5F3FF', icon: Stethoscope },
  anotacao: { label: 'Anotacao', color: '#0369A1', bg: '#F0F9FF', icon: Note },
  diagnostico: { label: 'Diagnostico', color: '#B45309', bg: '#FFFBEB', icon: Paperclip },
};

// ==================== RESUMO TECNICO ====================
const ResumoTecnico = ({ cliente, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    alergias: cliente.alergias || '',
    incompatibilidades_quimicas: cliente.incompatibilidades_quimicas || '',
    observacoes_tecnicas: cliente.observacoes_tecnicas || '',
    cuidados_principais: cliente.cuidados_principais || '',
  });

  const hasContent = form.alergias || form.incompatibilidades_quimicas || form.observacoes_tecnicas || form.cuidados_principais;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/clientes/${cliente.id}/resumo-tecnico`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setEditing(false);
      onUpdate();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'alergias', label: 'Alergias', icon: Warning, color: '#DC2626', placeholder: 'Ex: Amonia, Formol, Parabenos...' },
    { key: 'incompatibilidades_quimicas', label: 'Incompatibilidades Quimicas', icon: ShieldCheck, color: '#B45309', placeholder: 'Ex: Progressiva recente, Henê...' },
    { key: 'observacoes_tecnicas', label: 'Observacoes Tecnicas', icon: Eye, color: '#0369A1', placeholder: 'Ex: Cabelo fino, tendencia a quebra...' },
    { key: 'cuidados_principais', label: 'Cuidados Principais', icon: FirstAid, color: '#1B4332', placeholder: 'Ex: Evitar descoloracao, hidratar...' },
  ];

  if (!editing && !hasContent) {
    return (
      <div className="salonup-card p-6 text-center cursor-default hover:transform-none" data-testid="resumo-tecnico-empty">
        <FirstAid size={28} weight="light" className="mx-auto mb-3 text-[#A1A1AA]" />
        <p className="text-sm text-[#71717A] mb-3">Nenhuma informacao tecnica registrada</p>
        <button
          data-testid="btn-preencher-resumo"
          onClick={() => setEditing(true)}
          className="text-xs font-medium text-[#1B4332] hover:underline"
        >
          Preencher resumo tecnico
        </button>
      </div>
    );
  }

  return (
    <div className="salonup-card p-5 md:p-6 cursor-default hover:transform-none" data-testid="resumo-tecnico">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <FirstAid size={16} weight="regular" className="text-[#1B4332]" />
          <span className="text-xs font-semibold text-[#18181B] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
            Resumo Tecnico
          </span>
        </div>
        {!editing ? (
          <button
            data-testid="btn-editar-resumo"
            onClick={() => setEditing(true)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:bg-[#F4F4F5] hover:text-[#18181B] transition-all"
          >
            <PencilSimple size={16} weight="bold" />
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setEditing(false); setForm({ alergias: cliente.alergias || '', incompatibilidades_quimicas: cliente.incompatibilidades_quimicas || '', observacoes_tecnicas: cliente.observacoes_tecnicas || '', cuidados_principais: cliente.cuidados_principais || '' }); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:bg-[#F4F4F5] transition-all"
            >
              <X size={16} weight="bold" />
            </button>
            <button
              data-testid="btn-salvar-resumo"
              onClick={handleSave}
              disabled={saving}
              className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-xs font-medium text-white transition-all hover:shadow-md disabled:opacity-60"
              style={{ backgroundColor: '#1B4332' }}
            >
              {saving ? <Spinner size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
              Salvar
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {fields.map(({ key, label, icon: Icon, color, placeholder }) => (
          <div key={key}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Icon size={13} weight="regular" style={{ color }} />
              <label className="text-[10px] font-medium uppercase tracking-wide" style={{ color }}>{label}</label>
            </div>
            {editing ? (
              <textarea
                data-testid={`input-${key}`}
                value={form[key]}
                onChange={(e) => setForm(p => ({ ...p, [key]: e.target.value }))}
                placeholder={placeholder}
                rows={2}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 transition-all resize-none"
              />
            ) : (
              <p className="text-sm text-[#18181B] leading-relaxed pl-5">
                {form[key] || <span className="text-[#A1A1AA] italic">-</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== FICHA TECNICA FORM ====================
const FichaFormInline = ({ clienteId, tipo, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    data: new Date().toISOString().split('T')[0],
    titulo: '',
    descricao: '',
    profissional: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const tipoLabel = tipo === 'avaliacao' ? 'Nova Avaliacao' : tipo === 'diagnostico' ? 'Anexar Diagnostico' : 'Anotacao Tecnica';

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size <= 5 * 1024 * 1024) {
      setFile(f);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) return;
    setSaving(true);
    try {
      let anexo_data = '';
      let anexo_nome = '';
      let anexo_tipo = '';
      if (file) {
        const reader = new FileReader();
        const b64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        });
        anexo_data = b64;
        anexo_nome = file.name;
        anexo_tipo = file.type;
      }

      const res = await fetch(`${API}/clientes/${clienteId}/fichas-tecnicas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: clienteId,
          data: form.data,
          titulo: form.titulo,
          descricao: form.descricao,
          profissional: form.profissional,
          tipo,
          anexo_data,
          anexo_nome,
          anexo_tipo,
        }),
      });
      if (!res.ok) throw new Error();
      onSuccess();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  const cfg = TIPO_CONFIG[tipo] || TIPO_CONFIG.anotacao;
  const TipoIcon = cfg.icon;

  return (
    <form onSubmit={handleSubmit} className="p-5 bg-[#F7F6F4] rounded-2xl border border-[#E4E4E7] space-y-4" data-testid={`ficha-form-${tipo}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <TipoIcon size={16} weight="regular" style={{ color: cfg.color }} />
          <h4 className="text-sm font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{tipoLabel}</h4>
        </div>
        <button type="button" onClick={onCancel} className="text-[#A1A1AA] hover:text-[#18181B]">
          <X size={18} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data</label>
          <input
            data-testid="ficha-data"
            type="date"
            value={form.data}
            onChange={(e) => setForm(p => ({ ...p, data: e.target.value }))}
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional</label>
          <input
            data-testid="ficha-profissional"
            type="text"
            value={form.profissional}
            onChange={(e) => setForm(p => ({ ...p, profissional: e.target.value }))}
            placeholder="Nome"
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Titulo *</label>
        <input
          data-testid="ficha-titulo"
          type="text"
          value={form.titulo}
          onChange={(e) => setForm(p => ({ ...p, titulo: e.target.value }))}
          placeholder={tipo === 'avaliacao' ? 'Ex: Avaliacao capilar completa' : tipo === 'diagnostico' ? 'Ex: Teste de mecha' : 'Ex: Nota sobre coloracao'}
          className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
        />
      </div>

      <div>
        <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Descricao</label>
        <textarea
          data-testid="ficha-descricao"
          value={form.descricao}
          onChange={(e) => setForm(p => ({ ...p, descricao: e.target.value }))}
          placeholder="Detalhes..."
          rows={tipo === 'anotacao' ? 2 : 3}
          className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 resize-none"
        />
      </div>

      {/* File attachment for diagnostico or any type */}
      <div>
        <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">
          Anexo {tipo !== 'diagnostico' && '(opcional)'}
        </label>
        <label
          data-testid="ficha-anexo-input"
          className="flex items-center gap-2 px-3 py-2.5 bg-white border border-dashed border-[#D4D4D8] rounded-xl text-sm text-[#71717A] cursor-pointer hover:border-[#1B4332]/40 hover:bg-[#F7F6F4] transition-all"
        >
          <Paperclip size={16} weight="regular" />
          <span>{file ? file.name : 'Selecionar arquivo (max 5MB)'}</span>
          <input type="file" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" />
        </label>
      </div>

      <button
        type="submit"
        disabled={saving || !form.titulo.trim()}
        data-testid="btn-salvar-ficha"
        className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition-all hover:shadow-md disabled:opacity-60"
        style={{ backgroundColor: cfg.color }}
      >
        {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
        {saving ? 'Salvando...' : 'Salvar'}
      </button>
    </form>
  );
};

// ==================== HISTORICO TECNICO ====================
const HistoricoTecnico = ({ cliente, onRefresh }) => {
  const [activeForm, setActiveForm] = useState(null);
  const fichas = cliente.fichas_tecnicas || [];

  const handleDelete = async (fichaId) => {
    if (!window.confirm('Excluir este registro tecnico?')) return;
    try {
      await fetch(`${API}/clientes/${cliente.id}/fichas-tecnicas/${fichaId}`, { method: 'DELETE' });
      onRefresh();
    } catch {
      // silently fail
    }
  };

  const handleDownload = async (fichaId) => {
    const { downloadWithAuth } = await import('@/lib/apiClient');
    await downloadWithAuth(
      `${API}/clientes/${cliente.id}/fichas-tecnicas/${fichaId}/anexo`
    );
  };

  return (
    <div data-testid="historico-tecnico">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { tipo: 'avaliacao', label: 'Nova Avaliacao', Icon: Stethoscope },
          { tipo: 'anotacao', label: 'Anotacao Rapida', Icon: Note },
          { tipo: 'diagnostico', label: 'Anexar Diagnostico', Icon: Paperclip },
        ].map(({ tipo, label, Icon }) => {
          const cfg = TIPO_CONFIG[tipo];
          return (
            <button
              key={tipo}
              data-testid={`btn-${tipo}`}
              onClick={() => setActiveForm(activeForm === tipo ? null : tipo)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all border ${
                activeForm === tipo
                  ? 'text-white shadow-sm'
                  : 'hover:shadow-sm'
              }`}
              style={activeForm === tipo
                ? { backgroundColor: cfg.color, borderColor: cfg.color }
                : { backgroundColor: cfg.bg, borderColor: 'transparent', color: cfg.color }
              }
            >
              <Icon size={14} weight="bold" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Active Form */}
      {activeForm && (
        <div className="mb-5">
          <FichaFormInline
            clienteId={cliente.id}
            tipo={activeForm}
            onSuccess={() => { setActiveForm(null); onRefresh(); }}
            onCancel={() => setActiveForm(null)}
          />
        </div>
      )}

      {/* Timeline */}
      {fichas.length > 0 ? (
        <div className="space-y-2.5">
          {fichas.map((f) => {
            const cfg = TIPO_CONFIG[f.tipo] || TIPO_CONFIG.anotacao;
            const TipoIcon = cfg.icon;
            return (
              <div key={f.id} data-testid={`ficha-${f.id}`} className="salonup-card p-4 cursor-default hover:transform-none">
                <div className="flex items-start gap-3">
                  {/* Type Icon */}
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: cfg.bg }}
                  >
                    <TipoIcon size={16} weight="regular" style={{ color: cfg.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
                        {f.titulo}
                      </span>
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md uppercase tracking-wide"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#A1A1AA] mb-1.5">
                      <span>{formatDate(f.data)}</span>
                      {f.profissional && <span>{f.profissional}</span>}
                    </div>
                    {f.descricao && (
                      <p className="text-sm text-[#71717A] leading-relaxed">{f.descricao}</p>
                    )}
                    {(f.tem_anexo || f.anexo_nome) && (
                      <button
                        data-testid={`btn-download-${f.id}`}
                        onClick={() => handleDownload(f.id)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1B4332] hover:underline"
                      >
                        <DownloadSimple size={14} weight="bold" />
                        {f.anexo_nome || 'Baixar anexo'}
                      </button>
                    )}
                  </div>

                  {/* Delete */}
                  <button
                    data-testid={`btn-delete-ficha-${f.id}`}
                    onClick={() => handleDelete(f.id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:bg-red-50 hover:text-red-500 transition-all flex-shrink-0"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !activeForm && (
          <div className="salonup-card p-8 text-center cursor-default hover:transform-none">
            <FileText size={28} weight="light" className="mx-auto mb-3 text-[#A1A1AA]" />
            <p className="text-sm text-[#71717A]">Nenhum registro tecnico</p>
            <p className="text-xs text-[#A1A1AA] mt-1">Registre avaliacoes, anotacoes e diagnosticos</p>
          </div>
        )
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export const FichaTecnicaSection = ({ cliente, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('resumo');
  const fichasCount = (cliente.fichas_tecnicas || []).length;
  const hasResumo = cliente.alergias || cliente.incompatibilidades_quimicas || cliente.observacoes_tecnicas || cliente.cuidados_principais;

  return (
    <section className="mb-8" data-testid="ficha-tecnica-section">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText size={18} weight="regular" className="text-[#1B4332]" />
          <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
            Ficha Tecnica
          </h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[#F4F4F5] rounded-xl mb-5 w-fit" data-testid="ficha-tabs">
        <button
          data-testid="tab-resumo"
          onClick={() => setActiveTab('resumo')}
          className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === 'resumo'
              ? 'bg-white text-[#18181B] shadow-sm'
              : 'text-[#71717A] hover:text-[#18181B]'
          }`}
        >
          Resumo Tecnico
          {hasResumo && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-[#1B4332] inline-block" />}
        </button>
        <button
          data-testid="tab-historico"
          onClick={() => setActiveTab('historico')}
          className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === 'historico'
              ? 'bg-white text-[#18181B] shadow-sm'
              : 'text-[#71717A] hover:text-[#18181B]'
          }`}
        >
          Historico Tecnico
          {fichasCount > 0 && (
            <span className="ml-1.5 text-[10px] font-semibold bg-[#1B4332] text-white px-1.5 py-0.5 rounded-md">
              {fichasCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'resumo' && (
        <ResumoTecnico cliente={cliente} onUpdate={onRefresh} />
      )}
      {activeTab === 'historico' && (
        <HistoricoTecnico cliente={cliente} onRefresh={onRefresh} />
      )}
    </section>
  );
};

export default FichaTecnicaSection;
